import * as React from "react";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { Button } from "@/lib/shadcn/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/lib/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/lib/shadcn/ui/popover";
import { cn } from "@/lib/shadcn/utils";

export interface DropdownAction {
    label: string;
    onClick: () => void;
    icon?: React.FC<{ className?: string }>;
    iconClassName?: string; // Use Tailwind classes instead of direct colors
    caretClassName?: string; // Use Tailwind classes for caret
}

interface DropdownActionsProps {
    options: DropdownAction[];
    placeholder: string;
    variant?: "default" | "destructive" | "outline" | "secondary" | "volt" | "ghost" | "link";
    className?: string;
    width?: "auto" | "fit" | string; // Added "auto" and "fit" options
    showSearch?: boolean;
    defaultIconClassName?: string;
    minWidth?: string; // Optional minimum width
    maxWidth?: string; // Optional maximum width
    disabled?: boolean;
}

export const DropdownActions: React.FC<DropdownActionsProps> = ({
    options,
    placeholder,
    variant = "outline",
    className,
    width = "w-[200px]", // Default fixed width
    minWidth = "min-w-[200px]", // Default minimum width
    maxWidth = "max-w-[400px]", // Default maximum width
    showSearch = true,
    defaultIconClassName,
    disabled = false,
}) => {
    const [open, setOpen] = React.useState(false);

    // Helper function to determine width classes
    const getWidthClasses = () => {
        if (width === "auto") {
            return cn(minWidth, maxWidth, "w-auto");
        }
        if (width === "fit") {
            return cn(minWidth, maxWidth, "w-fit");
        }
        return width;
    };

    // Helper function to determine caret icon classes based on variant or custom classes
    const getCaretIconClasses = (variant: DropdownActionsProps["variant"], caretClassName?: string) => {
        const baseClasses = "ml-2 h-4 w-4 shrink-0";

        const variantClasses = {
            default: "text-primary-foreground",
            destructive: "text-destructive-foreground",
            outline: "text-foreground",
            secondary: "text-secondary-foreground",
            volt: "text-volt-foreground",
            ghost: "text-foreground",
            link: "text-primary",
        };

        return cn(
            baseClasses,
            caretClassName || variantClasses[variant || "outline"],
            "transition-transform duration-200",
            open && "transform rotate-180",
        );
    };

    // Helper function to get action icon classes with custom classes support
    const getActionIconClasses = (variant: DropdownActionsProps["variant"], iconClassName?: string) => {
        const baseClasses = "mr-2 h-4 w-4 shrink-0";

        // If custom classes are provided (either through the action or default), use them
        if (iconClassName || defaultIconClassName) {
            return cn(baseClasses, iconClassName || defaultIconClassName);
        }

        // Otherwise, fall back to variant-based classes
        return cn(baseClasses, {
            "text-primary-foreground": variant === "default",
            "text-destructive-foreground": variant === "destructive",
            "text-foreground": variant === "outline" || variant === "ghost",
            "text-secondary-foreground": variant === "secondary",
            "text-volt-foreground": variant === "volt",
            "text-primary": variant === "link",
        });
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    disabled={disabled}
                    variant={variant}
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        getWidthClasses(),
                        "justify-between",
                        "truncate", // Add truncate to prevent text overflow
                        className,
                    )}
                >
                    <span className="truncate">{placeholder}</span>
                    <CaretSortIcon className={getCaretIconClasses(variant, options.find((opt) => opt.caretClassName)?.caretClassName)} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn(getWidthClasses(), "p-0")}>
                <Command>
                    {showSearch && <CommandInput placeholder={placeholder} className="h-9" />}
                    <CommandList>
                        <CommandEmpty>No option found.</CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.label}
                                    value={option.label}
                                    onSelect={() => {
                                        setOpen(false);
                                        option.onClick();
                                    }}
                                    className="flex items-center cursor-pointer"
                                >
                                    {option.icon && <option.icon className={getActionIconClasses(variant, option.iconClassName)} />}
                                    <span className="truncate">{option.label}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};
